import { useEffect, useState, useRef } from "react";
import "../css/DeliveryGuysPage.css";
import {
    createDeliveryGuy,
    apiFetchDeliveryGuys,
    toggleDeliveryPartnerStatus,
    updateDeliveryGuy,
    updateDeliveryGuyInactiveStatus,
    fetchDeliveryLocations
} from "../service/api/orders";

const DELIVERY_GUY_INACTIVE_STATUSES = [
    { value: "inactive_cod_pending",  label: "COD Cash Pending" },
    { value: "inactive_on_leave",     label: "On Leave" },
    { value: "inactive_document_due", label: "Document Due" },
    { value: "inactive_suspended",    label: "Suspended" },
    { value: "inactive_onboarding",   label: "Onboarding" },
];

const TABS = [
    { key: "active",   label: "Active" },
    { key: "inactive", label: "Inactive" },
];

const InactiveStatusPopover = ({ boy, onClose, onSave }) => {
    const [selected, setSelected] = useState(boy.delivery_guy_detail?.inactive_status ?? null);
    const [saving, setSaving] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [onClose]);

    const handleSave = async () => {
        setSaving(true);
        try { await onSave(boy.id, selected); onClose(); }
        finally { setSaving(false); }
    };

    return (
        <div ref={ref} style={{ position: "absolute", zIndex: 999, background: "var(--color-surface, #fff)", border: "1px solid var(--color-border-secondary, #e2e8f0)", borderRadius: "var(--border-radius-lg, 10px)", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", padding: "12px", minWidth: 220, right: 0, top: "calc(100% + 6px)" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-secondary, #64748b)", marginBottom: 8 }}>Set Inactive Reason</div>
            <label style={optionStyle(selected === null)}>
                <input type="radio" name="inactive_status" value="" checked={selected === null} onChange={() => setSelected(null)} style={{ display: "none" }} />
                <span style={dotStyle(selected === null, "#10b981")} />
                <span style={{ fontSize: 13 }}>Active (clear reason)</span>
            </label>
            {DELIVERY_GUY_INACTIVE_STATUSES.map(({ value, label }) => (
                <label key={value} style={optionStyle(selected === value)}>
                    <input type="radio" name="inactive_status" value={value} checked={selected === value} onChange={() => setSelected(value)} style={{ display: "none" }} />
                    <span style={dotStyle(selected === value, "#f59e0b")} />
                    <span style={{ fontSize: 13 }}>{label}</span>
                </label>
            ))}
            <button onClick={handleSave} disabled={saving} style={{ marginTop: 10, width: "100%", padding: "7px 0", fontSize: 13, fontWeight: 600, borderRadius: "var(--border-radius-md, 6px)", border: "none", background: "var(--color-primary, #6366f1)", color: "#fff", cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving…" : "Apply"}
            </button>
        </div>
    );
};

const optionStyle = (active) => ({ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 6, cursor: "pointer", background: active ? "var(--color-surface-hover, #f1f5f9)" : "transparent", userSelect: "none" });
const dotStyle = (active, color) => ({ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: active ? color : "var(--color-border-secondary, #cbd5e1)", transition: "background 0.15s" });

// ─────────────────────────────────────────────────────────────────────────────

const DeliveryGuysPage = () => {

    const [deliveryGuys, setDeliveryGuys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [isUpdating, setIsUpdating] = useState([]);
    const [editingGuy, setEditingGuy] = useState(null);
    const [openPopover, setOpenPopover] = useState(null);
    const [activeTab, setActiveTab] = useState("active");

    const [deliveryLocations, setDeliveryLocations] = useState([]);

    const [search, setSearch] = useState("");
    const searchTimeout = useRef(null);

    const ITEMS_PER_PAGE = 20;

    useEffect(() => {
        loadDeliveryGuys(1, "");
    }, [activeTab]);


    useEffect(() => {
        fetchDeliveryLocations()
            .then((res) => {
                if (res.success) {
                    setDeliveryLocations(res.data || []);
                }
            })
            .catch((err) => console.error(err));
    }, []);

    const loadDeliveryGuys = async (page = 1, searchTerm = search) => {
        try {
            setLoading(true);
            const response = await apiFetchDeliveryGuys({
                page,
                limit: ITEMS_PER_PAGE,
                search: searchTerm,
                with_inactive: activeTab === "inactive" ? 1 : undefined,
            });
            setDeliveryGuys(response?.data || []);
            setCurrentPage(page);
            setTotalItems(response?.total || 0);
            setTotalPages(Math.ceil((response?.total || 0) / ITEMS_PER_PAGE));
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearch("");
        setCurrentPage(1);
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearch(value);
        clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => { loadDeliveryGuys(1, value); }, 400);
    };

    const toggleDeliveryGuyStatus = async (id, currentStatus) => {
        if (isUpdating.includes(id)) return;
        setIsUpdating((prev) => [...prev, id]);
        try {
            await toggleDeliveryPartnerStatus(id, currentStatus);
            setDeliveryGuys((prev) => prev.map((d) => d.id === id ? { ...d, delivery_guy_detail: { ...d.delivery_guy_detail, status: currentStatus ? 0 : 1 } } : d));
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.message || "Something went wrong");
        } finally {
            setIsUpdating((prev) => prev.filter((updatingId) => updatingId !== id));
        }
    };

    const handleSaveInactiveStatus = async (id, inactiveStatus) => {
        try {
            await updateDeliveryGuyInactiveStatus(id, inactiveStatus);
            if (activeTab === "inactive" && inactiveStatus === null) {
                setDeliveryGuys((prev) => prev.filter((d) => d.id !== id));
            } else {
                setDeliveryGuys((prev) => prev.map((d) => d.id === id ? { ...d, delivery_guy_detail: { ...d.delivery_guy_detail, inactive_status: inactiveStatus } } : d));
            }
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.message || "Something went wrong");
        }
    };

    const handleSaveDeliveryGuy = async (formData, editId = null) => {
        try {
            const response = editId ? await updateDeliveryGuy(editId, formData) : await createDeliveryGuy(formData);
            alert(response?.message || "Saved");
            setModal(false);
            setEditingGuy(null);
            loadDeliveryGuys(currentPage, search);
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.message || "Something went wrong");
        }
    };

    const isInactiveTab = activeTab === "inactive";
    const colSpanCount = isInactiveTab ? 13 : 14;

    return (
        <div className="delivery-guys-page">

            <div className="delivery-guys-head">
                <div>
                    <div className="delivery-guys-title">Delivery Guys</div>
                    <p className="delivery-guys-subtitle">Create and manage delivery personnel</p>
                </div>
                <button className="delivery-guys-create-btn" onClick={() => setModal(true)}>
                    <IconPlus size={14} /> Add Delivery Guy
                </button>
            </div>

            <div className="delivery-guys-section">

                {/* Tabs */}
                <div className="delivery-guys-tabs">
                    {TABS.map((tab) => (
                        <button
                            key={tab.key}
                            className={`delivery-guys-tab${activeTab === tab.key ? " active" : ""}`}
                            onClick={() => handleTabChange(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="delivery-guys-toolbar">
                    <div className="delivery-guys-search-wrap">
                        <span className="delivery-guys-search-icon"><IconSearch size={14} /></span>
                        <input className="delivery-guys-search" type="text" value={search} onChange={handleSearch} placeholder="Search by name or email..." />
                        {search && (
                            <button className="delivery-guys-search-clear" onClick={() => { setSearch(""); loadDeliveryGuys(1, ""); }}>
                                <IconX size={12} />
                            </button>
                        )}
                    </div>
                    <span className="delivery-guys-count-label">{totalItems} Delivery Guys</span>
                </div>

                <div className="delivery-guys-table-wrap">
                    <table className="delivery-guys-table">
                        <thead className="delivery-guys-thead">
                        <tr>
                            <th className="delivery-guys-th">Name</th>
                            <th className="delivery-guys-th">Email</th>
                            <th className="delivery-guys-th">Phone</th>
                            <th className="delivery-guys-th">Referral Code</th>
                            {!isInactiveTab && <th className="delivery-guys-th">Status</th>}
                            <th className="delivery-guys-th">Inactive Reason</th>
                            <th className="delivery-guys-th">Delivery Name</th>
                            <th className="delivery-guys-th">Location</th>
                            <th className="delivery-guys-th">Priority</th>
                            <th className="delivery-guys-th">Age</th>
                            <th className="delivery-guys-th">Vehicle Number</th>
                            <th className="delivery-guys-th">Surge Commission %</th>
                            <th className="delivery-guys-th">Created At</th>
                            <th className="delivery-guys-th">Actions</th>
                        </tr>
                        </thead>
                        <tbody className="delivery-guys-tbody">

                        {loading ? (
                            Array.from({ length: 5 }).map((_, index) => (
                                <tr key={`skeleton-${index}`} className="delivery-guys-skeleton-row">
                                    {Array.from({ length: colSpanCount }).map((__, cellIndex) => (
                                        <td key={cellIndex} className="delivery-guys-skeleton-cell">
                                            <div className="delivery-guys-skeleton-block" />
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : deliveryGuys?.length > 0 ? (

                            deliveryGuys.map((boy) => {
                                const inactiveStatus = boy.delivery_guy_detail?.inactive_status ?? null;
                                const inactiveLabel = DELIVERY_GUY_INACTIVE_STATUSES.find((s) => s.value === inactiveStatus)?.label ?? null;

                                return (
                                    <tr key={boy.id} className="delivery-guys-row">
                                        <td className="delivery-guys-td">{boy.name}</td>
                                        <td className="delivery-guys-td">{boy.email}</td>
                                        <td className="delivery-guys-td">{boy.phone}</td>
                                        <td className="delivery-guys-td">{boy.referral_code || boy.refer_code}</td>

                                        {!isInactiveTab && (
                                            <td className="delivery-guys-td">
                                                <button
                                                    type="button"
                                                    className={`delivery-guys-status-toggle ${boy.delivery_guy_detail?.status === 1 ? "active" : "inactive"}`}
                                                    onClick={() => toggleDeliveryGuyStatus(boy.id, boy.delivery_guy_detail?.status === 1)}
                                                    disabled={isUpdating.includes(boy.id)}
                                                >
                                                    <span className="delivery-guys-status-switch"/>
                                                    <span className="delivery-guys-status-label">
                                                            {isUpdating.includes(boy.id) ? "Updating..." : boy.delivery_guy_detail?.status === 1 ? "Online" : "Offline"}
                                                        </span>
                                                </button>
                                            </td>
                                        )}

                                        <td className="delivery-guys-td">
                                            <div style={{
                                                position: "relative",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 6
                                            }}>
                                                    <span style={{
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                        padding: "2px 8px",
                                                        borderRadius: 20,
                                                        whiteSpace: "nowrap",
                                                        background: inactiveLabel ? "#fef3c7" : "transparent",
                                                        color: inactiveLabel ? "#92400e" : "var(--color-text-muted, #94a3b8)",
                                                        border: inactiveLabel ? "1px solid #fde68a" : "none"
                                                    }}>
                                                        {inactiveLabel ?? "—"}
                                                    </span>
                                                <button type="button" title="Set inactive reason"
                                                        onClick={() => setOpenPopover(openPopover === boy.id ? null : boy.id)}
                                                        style={{
                                                            background: "none",
                                                            border: "0.5px solid var(--color-border-secondary, #e2e8f0)",
                                                            borderRadius: "var(--border-radius-md, 6px)",
                                                            padding: "3px 6px",
                                                            cursor: "pointer",
                                                            color: "var(--color-text-secondary, #64748b)",
                                                            display: "flex",
                                                            alignItems: "center"
                                                        }}>
                                                    <IconEdit size={12}/>
                                                </button>
                                                {openPopover === boy.id && (
                                                    <InactiveStatusPopover boy={boy}
                                                                           onClose={() => setOpenPopover(null)}
                                                                           onSave={handleSaveInactiveStatus}/>
                                                )}
                                            </div>
                                        </td>

                                        <td className="delivery-guys-td">{boy.delivery_guy_detail?.name || "-"}</td>
                                        <td className="delivery-guys-td">
                                            {boy.delivery_guy_detail?.delivery_location?.name || "-"}
                                        </td>

                                        <td className="delivery-guys-td">
                                            {boy.delivery_guy_detail?.delivery_location_priority ?? "-"}
                                        </td>
                                        <td className="delivery-guys-td">{boy.delivery_guy_detail?.age || "-"}</td>
                                        <td className="delivery-guys-td">{boy.delivery_guy_detail?.vehicle_number || "-"}</td>
                                        <td className="delivery-guys-td">{boy.delivery_guy_detail?.surge_commission_rate || "-"}</td>
                                        <td className="delivery-guys-td" style={{whiteSpace: "nowrap"}}>
                                            {new Date(boy.created_at).toLocaleString("en-GB", {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                hour12: false
                                            })}
                                        </td>
                                        <td className="delivery-guys-td">
                                            <button type="button" onClick={() => setEditingGuy(boy)} style={{
                                                background: "none",
                                                border: "0.5px solid var(--color-border-secondary)",
                                                borderRadius: "var(--border-radius-md)",
                                                padding: "4px 8px",
                                                cursor: "pointer",
                                                color: "var(--color-text-secondary)",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 4
                                            }}>
                                                <IconEdit size={13}/> Edit
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })

                        ) : !loading ? (
                            <tr>
                                <td colSpan={colSpanCount} className="delivery-guys-empty">
                                    <div className="delivery-guys-empty__icon">🚚</div>
                                    <div className="delivery-guys-empty__title">{isInactiveTab ? "No inactive delivery guys" : "No delivery guys found"}</div>
                                    <div className="delivery-guys-empty__sub">{isInactiveTab ? "All riders are currently active" : "Add your first delivery guy"}</div>
                                </td>
                            </tr>
                        ) : null}

                        </tbody>
                    </table>
                </div>

                <div className="delivery-guys-pagination">
                    <div className="delivery-guys-pagination__info">
                        Page {currentPage} of {totalPages} | Total: {totalItems} Delivery Guys
                    </div>
                    <div className="delivery-guys-pagination__controls">
                        <button className="delivery-guys-pagination__btn" onClick={() => loadDeliveryGuys(currentPage - 1)} disabled={currentPage === 1 || loading}>← Previous</button>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            const pageNum = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                            if (pageNum > totalPages) return null;
                            return (
                                <button key={pageNum} className={`delivery-guys-pagination__page ${pageNum === currentPage ? "active" : ""}`} onClick={() => loadDeliveryGuys(pageNum)} disabled={loading}>
                                    {pageNum}
                                </button>
                            );
                        })}
                        <button className="delivery-guys-pagination__btn" onClick={() => loadDeliveryGuys(currentPage + 1)} disabled={currentPage === totalPages || loading}>Next →</button>
                    </div>
                </div>
            </div>

            {(modal || editingGuy) && (
                <DeliveryGuyModal
                    onClose={() => {
                        setModal(false);
                        setEditingGuy(null);
                    }}
                    onSave={handleSaveDeliveryGuy}
                    initialData={editingGuy}
                    deliveryLocations={deliveryLocations}
                />
            )}
        </div>
    );
};

// ── DeliveryGuyModal — unchanged ──────────────────────────────────────────────
const DeliveryGuyModal = ({ onClose, onSave, initialData = null, deliveryLocations = [] }) => {
    const firstRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const isEdit = !!initialData;

    const [form, setForm] = useState({
        name: initialData?.name ?? "",
        email: initialData?.email ?? "",
        phone: initialData?.phone ?? "",
        password: "",

        delivery_name: initialData?.delivery_guy_detail?.name ?? "",
        delivery_age: initialData?.delivery_guy_detail?.age ?? "",
        delivery_description: initialData?.delivery_guy_detail?.description ?? "",
        delivery_vehicle_number: initialData?.delivery_guy_detail?.vehicle_number ?? "",

        delivery_location_id: initialData?.delivery_guy_detail?.delivery_location_id ?? "",
        delivery_location_priority: initialData?.delivery_guy_detail?.delivery_location_priority ?? 100,

        refercode: "",
        surge_commission_rate: initialData?.delivery_guy_detail?.surge_commission_rate ?? "",
        delivery_photo: null,
    });

    useEffect(() => { firstRef.current?.focus(); }, []);
    useEffect(() => {
        const handler = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    const set = (field) => (e) => {
        const value = field === "delivery_photo" ? e.target.files[0] : e.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const required = [{ key: "name", label: "Name" }, { key: "phone", label: "Phone" }, { key: "delivery_name", label: "Delivery Name" }, { key: "delivery_age", label: "Age" }];
        if (!isEdit) { required.push({ key: "password", label: "Password" }); required.push({ key: "email", label: "Email" }); }
        for (const field of required) {
            if (!form[field.key]?.toString().trim()) { alert(`${field.label} is required.`); return; }
        }
        setSaving(true);
        try {
            const formData = new FormData();
            Object.keys(form).forEach((key) => { if (form[key] !== null && form[key] !== "") formData.append(key, form[key]); });
            await onSave(formData, isEdit ? initialData.id : null);
        } finally { setSaving(false); }
    };

    return (
        <div className="delivery-guys-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="delivery-guys-modal">
                <div className="delivery-guys-modal-header">
                    <div className="delivery-guys-modal-title">{isEdit ? "Edit Delivery Guy" : "Add Delivery Guy"}</div>
                    <button className="delivery-guys-modal-close" onClick={onClose}><IconX size={13} /></button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="delivery-guys-modal-body">
                        <div className="delivery-guys-field"><label className="delivery-guys-label">Name *</label><input
                            ref={firstRef} className="delivery-guys-input" value={form.name} onChange={set("name")}
                            placeholder="Enter name" required/></div>
                        <div className="delivery-guys-field"><label className="delivery-guys-label">Email
                            *</label><input type="email" className="delivery-guys-input" value={form.email}
                                            onChange={set("email")} placeholder="Enter email" required/></div>
                        <div className="delivery-guys-field"><label className="delivery-guys-label">Phone
                            *</label><input className="delivery-guys-input" value={form.phone} onChange={set("phone")}
                                            placeholder="Enter phone" required/></div>
                        <div className="delivery-guys-field">
                            <label className="delivery-guys-label">Password *</label>
                            <div style={{position: "relative", display: "flex", alignItems: "center"}}>
                                <input type={showPassword ? "text" : "password"} className="delivery-guys-input"
                                       value={form.password} onChange={set("password")} placeholder="Enter password"
                                       required={!isEdit} style={{paddingRight: "42px"}}/>
                                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{
                                    position: "absolute",
                                    right: "12px",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    color: "#94a3b8"
                                }}>
                                    {showPassword ? (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                             strokeLinejoin="round">
                                            <path
                                                d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                                            <path
                                                d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                                            <line x1="1" y1="1" x2="23" y2="23"/>
                                        </svg>
                                    ) : (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                             strokeLinejoin="round">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                            <circle cx="12" cy="12" r="3"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>
                        <div className="delivery-guys-field"><label className="delivery-guys-label">Delivery Name
                            *</label><input className="delivery-guys-input" value={form.delivery_name}
                                            onChange={set("delivery_name")} placeholder="Delivery name" required/></div>
                        <div className="delivery-guys-field"><label className="delivery-guys-label">Age</label><input
                            type="number" min="18" max="50" required className="delivery-guys-input"
                            value={form.delivery_age} onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || Number(value) <= 50) setForm((prev) => ({
                                ...prev,
                                delivery_age: value
                            }));
                        }} placeholder="Age"/></div>
                        <div className="delivery-guys-field"><label className="delivery-guys-label">Vehicle
                            Number</label><input className="delivery-guys-input" value={form.delivery_vehicle_number}
                                                 onChange={set("delivery_vehicle_number")}
                                                 placeholder="Vehicle Number"/></div>

                        <div className="delivery-guys-field">
                            <label className="delivery-guys-label">Delivery Location</label>
                            <select
                                className="delivery-guys-input"
                                value={form.delivery_location_id}
                                onChange={set("delivery_location_id")}
                            >
                                <option value="">Select delivery location</option>
                                {deliveryLocations.map((location) => (
                                    <option key={location.id} value={location.id}>
                                        {location.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="delivery-guys-field">
                            <label className="delivery-guys-label">Location Priority</label>
                            <input
                                type="number"
                                min="1"
                                max="999"
                                className="delivery-guys-input"
                                value={form.delivery_location_priority}
                                onChange={set("delivery_location_priority")}
                                placeholder="Default: 100"
                            />
                        </div>

                        {(!isEdit || !initialData?.referral_by) && (
                            <div className="delivery-guys-field"><label className="delivery-guys-label">Referral
                                Code</label><input className="delivery-guys-input" value={form.refercode}
                                                   onChange={set("refercode")} placeholder="Optional referral code"/>
                            </div>)}
                        <div className="delivery-guys-field"><label
                            className="delivery-guys-label">Description</label><textarea className="delivery-guys-input"
                                                                                         value={form.delivery_description}
                                                                                         onChange={set("delivery_description")}
                                                                                         placeholder="Description"/>
                        </div>
                        <div className="delivery-guys-field"><label className="delivery-guys-label">Surge Commission
                            %</label><input type="number" min="0" max="100" className="delivery-guys-input"
                                            value={form.surge_commission_rate} onChange={set("surge_commission_rate")}
                                            placeholder="Default: 100%"/></div>
                        <div className="delivery-guys-field"><label className="delivery-guys-label">Photo</label><input
                            type="file" onChange={set("delivery_photo")}/></div>
                        <div style={{height: "10px", margin: "16px 0"}}/>
                    </div>
                    <div className="delivery-guys-modal-footer">
                        <button type="submit" className="delivery-guys-submit-btn"
                                disabled={saving}>{saving ? "Saving..." : isEdit ? "Save Changes" : "Add Delivery Guy"}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const IconPlus = ({size = 16}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
         strokeLinecap="round">
        <line x1="12" y1="5" x2="12" y2="19"/>
        <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>);
const IconX = ({size = 14}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
         strokeLinecap="round">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>);
const IconEdit = ({size = 16}) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>);
const IconSearch = ({ size = 16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>);

export default DeliveryGuysPage;